import { AuthService } from './authService';
import type { LabelContext } from '../types/labelDesign';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Types matching backend entities
export interface ReportConfiguration {
  id: number;
  companyNo: string;
  reportID: number;
  reportType: number;
  reportName: string;
  description: string;
  printerName?: string;
  cloudStorageYesNo: boolean;
  cloudStorageItemLabelFolder?: string;
  externalShare?: string;
  externalFilemask?: string;
  externalPrinterWidth?: number;
  externalPrinterLength?: number;
  externalPrinterLandscape?: boolean;
  /**
   * JSON-serialisert layout fra LabelDesigner (canvas-editor).
   * Null = bruk innebygd hardkodet generator. Satt = custom design.
   */
  layoutJson?: string | null;
  /** Bestemmer om rapporten vises i utskriftsdialoger. Default true. */
  activeYesNo: boolean;
  updatedBy?: string;
  lastUpdate?: string;
  createdBy?: string;
  created?: string;
}

export interface StandardReportInfo {
  reportName: string;
  reportType: number;
  displayName: string;
  description: string;
  paperWidth: number;
  paperHeight: number;
  landscape: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export enum ReportType {
  // 1-5: Vare/Lokasjon -> Profile.LabelPrinterName
  ItemLabel = 1,
  ItemAndPurchaseOrder = 2,
  ItemAndSalesOrder = 3,
  LocationLabel = 4,
  LocationLabelAlt = 5,
  // 10-19: Pakkseddel -> Profile.PackageSlipPrinterName
  PackingSlip = 10,
  PackingSlipDetailed = 11,
  // 20-29: Fraktetikett -> Profile.ShippingLabelsPrinterName
  ShippingLabel = 20,
  // 30-39: Fraktbrev -> Profile.ConsignmentNotePrinterName
  ConsignmentNote = 30,
  // 40-49: Kollietikett -> Profile.PackagePrinterID
  PackageLabel = 40,
  BatchPackageLabel = 41,
  // 50-59: SSCC/pall -> Profile.STAND005PrinterName
  SSCCLabel = 50,
  SSCCTransportLabel = 51,
  // 70-79: Sorteringskasse -> Profile.StcSortingBoxPrinterName
  SortingBoxLabel = 70,
  // 99: Ekstern -> Profile.LabelPrinterName
  External = 99
}

export const ReportTypeLabels: Record<ReportType, string> = {
  [ReportType.ItemLabel]: 'Vareetikett',
  [ReportType.ItemAndPurchaseOrder]: 'Vareetikett med innkjøpsordre',
  [ReportType.ItemAndSalesOrder]: 'Vareetikett med salgsordre',
  [ReportType.LocationLabel]: 'Lokasjonsetikett',
  [ReportType.LocationLabelAlt]: 'Lokasjonsetikett (alternativ)',
  [ReportType.PackingSlip]: 'Pakkseddel',
  [ReportType.PackingSlipDetailed]: 'Pakkseddel (detaljert)',
  [ReportType.ShippingLabel]: 'Fraktetikett',
  [ReportType.ConsignmentNote]: 'Fraktbrev',
  [ReportType.PackageLabel]: 'Kollietikett',
  [ReportType.BatchPackageLabel]: 'Batch-pakkeetikett',
  [ReportType.SSCCLabel]: 'SSCC/pall-etikett',
  [ReportType.SSCCTransportLabel]: 'SSCC transport-etikett',
  [ReportType.SortingBoxLabel]: 'Sorteringskasse-etikett',
  [ReportType.External]: 'Ekstern'
};

/**
 * Groups of report types by printer routing (based on PrintConfigurationService).
 * Useful for displaying categorized dropdowns in admin UIs.
 */
export const ReportTypeGroups: { groupName: string; types: ReportType[] }[] = [
  { groupName: 'Vare/Lokasjon', types: [ReportType.ItemLabel, ReportType.ItemAndPurchaseOrder, ReportType.ItemAndSalesOrder, ReportType.LocationLabel, ReportType.LocationLabelAlt] },
  { groupName: 'Pakkseddel', types: [ReportType.PackingSlip, ReportType.PackingSlipDetailed] },
  { groupName: 'Fraktetikett', types: [ReportType.ShippingLabel] },
  { groupName: 'Fraktbrev', types: [ReportType.ConsignmentNote] },
  { groupName: 'Kolli', types: [ReportType.PackageLabel, ReportType.BatchPackageLabel] },
  { groupName: 'SSCC/Pall', types: [ReportType.SSCCLabel, ReportType.SSCCTransportLabel] },
  { groupName: 'Sorteringskasse', types: [ReportType.SortingBoxLabel] },
  { groupName: 'Annet', types: [ReportType.External] }
];

class ReportConfigurationService {
  /**
   * Get all report configurations for current tenant
   */
  async getAll(companyNo: string): Promise<ReportConfiguration[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch report configurations');
    }

    return await response.json();
  }

  /**
   * Get report configuration by ID
   */
  async getById(companyNo: string, id: number): Promise<ReportConfiguration> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch report ${id}`);
    }

    return await response.json();
  }

  /**
   * Get report configurations by type
   */
  async getByType(companyNo: string, reportType: number): Promise<ReportConfiguration[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/type/${reportType}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch reports for type ${reportType}`);
    }

    return await response.json();
  }

  /**
   * Get catalog of all available standard reports
   */
  async getStandardReportCatalog(companyNo: string): Promise<StandardReportInfo[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/standard-catalog`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch standard report catalog');
    }

    return await response.json();
  }

  /**
   * Get standard reports for a specific report type
   */
  async getStandardReportsByType(companyNo: string, reportType: number): Promise<StandardReportInfo[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/standard-catalog/type/${reportType}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch standard reports for type ${reportType}`);
    }

    return await response.json();
  }

  /**
   * Generate preview PDF for a report configuration
   */
  async getPreview(companyNo: string, reportId: number): Promise<Blob> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${reportId}/preview`
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to generate preview: ${error}`);
    }

    return await response.blob();
  }

  /**
   * Generate preview PDF fra et custom layout (LabelDesigner canvas-editor).
   * Sender layout + valgfri DB-kontekst + valgfri sampleData for overstyring.
   * Backend henter faktiske rader fra Items/Orders/OrderLine/POrders/POrderLine/StcBin
   * når kontekst er satt, slik at `{{Table.Column}}` plassholdere gir reelle verdier.
   */
  async getCustomLabelPreview(
    companyNo: string,
    layout: unknown,
    sampleData?: Record<string, string | null | undefined>,
    context?: LabelContext
  ): Promise<Blob> {
    const labelApi = API_BASE_URL.replace(/\/api\/v1$/, '/api/v1');
    const response = await AuthService.authenticatedPost(
      `${labelApi}/labelprinting/${companyNo}/preview-custom`,
      { layout, sampleData, context }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to generate custom label preview: ${error}`);
    }

    return await response.blob();
  }

  /**
   * Generate preview PDF for a standard report by name
   */
  async getStandardReportPreview(companyNo: string, reportName: string): Promise<Blob> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/standard-preview?reportName=${encodeURIComponent(reportName)}`
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to generate standard report preview: ${error}`);
    }

    return await response.blob();
  }

  /**
   * Create new report configuration
   */
  async create(companyNo: string, report: Partial<ReportConfiguration>): Promise<ReportConfiguration> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration`,
      report
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to create report configuration');
    }

    return await response.json();
  }

  /**
   * Update existing report configuration
   */
  async update(companyNo: string, id: number, report: ReportConfiguration): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}`,
      report
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to update report configuration');
    }
  }

  /**
   * Kopier rapport. Backend tildeler neste ledige ReportID og prefikser beskrivelsen
   * med "Kopi av ". Returnerer den nye raden.
   */
  async copy(companyNo: string, id: number): Promise<ReportConfiguration> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}/copy`,
      {}
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to copy report configuration');
    }

    return await response.json();
  }

  /**
   * Delete report configuration
   */
  async delete(companyNo: string, id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/${id}`
    );

    if (!response.ok) {
      throw new Error('Failed to delete report configuration');
    }
  }

  /**
   * Validate report configuration without saving
   */
  async validate(companyNo: string, report: Partial<ReportConfiguration>): Promise<ValidationResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/ReportConfiguration/validate`,
      report
    );

    if (!response.ok) {
      // Hent detaljert feil fra ASP.NET ProblemDetails / ModelState
      const body = await response.text();
      try {
        const parsed = JSON.parse(body);
        const modelErrors = parsed.errors
          ? Object.entries(parsed.errors).map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`).join('; ')
          : parsed.title || parsed.message || body;
        throw new Error(`Validering feilet (${response.status}): ${modelErrors}`);
      } catch (e) {
        if (e instanceof Error && e.message.startsWith('Validering feilet')) throw e;
        throw new Error(`Validering feilet (${response.status}): ${body || response.statusText}`);
      }
    }

    return await response.json();
  }
}

export default new ReportConfigurationService();
